// @flow
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	resetCart,
	toggleResetCartDlg,
	clearLastTransaction,
	addLastTransaction,
	saveCart,
	fetchCart,
	updateCartSuccess,
	throttleSaveCart,
} from '../actions/cart';
import type { CartData } from '../actions/types';
import type { CartState } from '../reducers/cart';
import { updateSkuPurchareCount } from '../actions/skus';
import { setCookie } from '../utils';
import useOrder from './orders';
import useFirebase from './firebase';
import { updateVSkuPurchareCount } from '../actions/virtual-shelf';
import { VSHELF_MACHINE_SKU } from '../constants';
import usePod from './pods';
import useAuth from './auth'

const useCart = () => {
	const cart: CartState = useSelector((state): CartState => state.cart);
	const { mobile = '' } = useSelector(state => state.user);
	const [{ sessionId = '' } = {}, , , , toggleSignupDlg] = useAuth();

	const { lastTransaction, imei = '', sku_json = {}, totalAmount } = cart || {};
	const dispatch = useDispatch();
	const [{ pendingOrders = [] } = {}, , , , , , , cancelPendingOrder] = useOrder();
	const [logEvent] = useFirebase();
	const [{ selectedPod, affiliateActive, affiliateData } = {}] = usePod();


	const saveCartCb = useCallback(
		(throttle: boolean = true) => {
			const { affiliation_info } = affiliateData || {};
			if (throttle) {
				return dispatch(throttleSaveCart(mobile, sessionId, 'PRODUCTS', affiliateActive ? affiliation_info : undefined));
			}
			dispatch(saveCart(mobile, sessionId, 'CART', affiliateActive ? affiliation_info : undefined));
		},
		[affiliateActive, affiliateData, dispatch, mobile, sessionId]
	);

	const cartSkuLength = useRef(0);
	useEffect(() => {
		cartSkuLength.current = Object.values(sku_json).reduce((total, cartSku = {}) => {
			if (typeof cartSku === 'object' && cartSku) {
				const { skus = [] } = cartSku;
				if (Array.isArray(skus)) {
					total += skus.length;
				}
			}
			return total;
		}, 0);
	}, [sku_json]);

	const pendingOrdersRef = useRef(pendingOrders && pendingOrders.length > 0);
	useMemo(() => {
		pendingOrdersRef.current = pendingOrders && pendingOrders.length > 0;
	}, [pendingOrders]);

	const updateCartCb = useCallback(
		(data: CartData, cartPage: boolean = false) => {
			const { skuData: { isVirtualSku = false, action, name} = {}, amount} = data;
			if (!mobile) {
				dispatch(addLastTransaction(data));
				toggleSignupDlg(true);
				return logEvent('add_to_cart_logout', { amount, sku_name: name });
			}

			if (pendingOrdersRef.current && !isVirtualSku) {
				dispatch(addLastTransaction(data));
				return dispatch(toggleResetCartDlg(true, 'ACTIVE_ORDER'));
			}

			if (imei && data && data.imei && data.imei !== imei && cartSkuLength.current > 0) {
				dispatch(addLastTransaction(data));
				return dispatch(toggleResetCartDlg(true, 'CART'));
			}

			dispatch(updateCartSuccess(data));
			if (!cartPage) {
				// lastTransaction && dispatch(clearLastTransaction());
				if (isVirtualSku) {
					dispatch(updateVSkuPurchareCount(data));
					if (action === VSHELF_MACHINE_SKU) {
						dispatch(updateSkuPurchareCount(data));
					}
				} else {
					dispatch(updateSkuPurchareCount(data));
					if (action === VSHELF_MACHINE_SKU) {
						dispatch(updateVSkuPurchareCount(data));
					}
				}
				saveCartCb(true);
			} else {
				saveCartCb(false);
			}
		},
		[dispatch, imei, logEvent, mobile, saveCartCb, toggleSignupDlg]
	);

	const handleResetCart = useCallback(
		(data: boolean, type: 'CART' | 'ACTIVE_ORDER') => {
			if (data) {
				if (type === 'ACTIVE_ORDER') {
					cancelPendingOrder(pendingOrders[0].order_id, true);
					const date = new Date(pendingOrders[0].last_modified_at || pendingOrders[0].created_at);
					const time = Math.floor((Date.now() - date.getTime()) / 1000);
					return logEvent('cancel_active_order', { time_elapsed: time, amount: pendingOrders[0].pre_auth_amount });
				}
				dispatch(resetCart());
				if (lastTransaction) {
					dispatch(updateCartSuccess(lastTransaction));
					dispatch(clearLastTransaction());
					const { skuData: { isVirtualSku = false, action } = {} } = lastTransaction;
					if (isVirtualSku) {
						dispatch(updateVSkuPurchareCount(lastTransaction));
						if (action === VSHELF_MACHINE_SKU) {
							dispatch(updateSkuPurchareCount(lastTransaction));
						}
					} else {
						dispatch(updateSkuPurchareCount(lastTransaction));
						if (action === VSHELF_MACHINE_SKU) {
							dispatch(updateVSkuPurchareCount(lastTransaction));
						}
					}
					saveCartCb(true);
				}
				dispatch(toggleResetCartDlg(false));
				logEvent('reset_cart', { amount: totalAmount });
			} else {
				dispatch(toggleResetCartDlg(data));
				lastTransaction && dispatch(clearLastTransaction());
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[cancelPendingOrder, dispatch, lastTransaction, pendingOrders, totalAmount]
	);

	const toggleResetCartDlgCb = useCallback(
		(data: boolean, type: 'ACTIVE_ORDER' | 'CART') => {
			dispatch(toggleResetCartDlg(data, type));
		},
		[dispatch]
	);

	const fetchCartCb = useCallback((activeAffl: boolean = true) => {
		let { imei } = cart;
			const { vm_imei_id, affiliation_info } = affiliateData || {};
		if (!imei) {
			if (activeAffl && affiliateActive) {
				imei = vm_imei_id;
			} else if (selectedPod) {
				const { vm_imei_id } = selectedPod;
				imei = vm_imei_id;
			}
		}
		dispatch(fetchCart({...cart, imei}, mobile, sessionId, affiliateActive ? affiliation_info : undefined));
	}, [cart, dispatch, mobile, sessionId, affiliateActive, selectedPod, affiliateData]);

	const resetCartCb = useCallback(() => {
		dispatch(resetCart());
	}, [dispatch]);

	useMemo(() => {
		setCookie('cart', JSON.stringify(cart));
	}, [cart]);

	return [cart, updateCartCb, handleResetCart, toggleResetCartDlgCb, saveCartCb, fetchCartCb, resetCartCb];
};

export default useCart;
